import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, MapPin, HeartPulse, ChevronRight } from "lucide-react";
import ShiftsLayout from "../../components/shifts/ShiftsLayout";
import PageMeta from "../../components/seo/PageMeta";
import { TOP_CITIES, SEO_SPECIALTIES } from "../../data/seoLandingData";

const SITE = "https://www.tritalcare.com";

/* ------------------------------------------------------------------ */
/*  PRN Shifts SEO Hub — /shiftbridge/prn-shifts                       */
/*  Internal-linking spine for all ~338 generated landing pages.       */
/*  Crawler-friendly directory grouped by Specialty / Location / Combo */
/* ------------------------------------------------------------------ */

export default function SeoHub() {
  const url = `${SITE}/shiftbridge/prn-shifts`;
  const ogImage = `${SITE}/images/logos/trital-shiftbridge-logo-transparent-master.png`;

  /* Group cities by region for clean directory display */
  const regions = {};
  TOP_CITIES.forEach((c) => {
    if (!regions[c.region]) regions[c.region] = [];
    regions[c.region].push(c);
  });

  const totalUrls = SEO_SPECIALTIES.length + TOP_CITIES.length + SEO_SPECIALTIES.length * TOP_CITIES.length;

  return (
    <ShiftsLayout>
      <PageMeta
        title="PRN Shifts — All Specialties × Cities Directory | TRITAL ShiftBridge™"
        description={`Browse every PRN shift category on TRITAL ShiftBridge™ — ${SEO_SPECIALTIES.length} specialties × ${TOP_CITIES.length} top US healthcare markets. RN, ICU, ER, Med-Surg, L&D, NICU, Therapy, LPN, CNA. W-2 weekly pay. Find PRN shifts near you.`}
        canonical={url}
        ogImage={ogImage}
        schema={[
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home",        "item": `${SITE}/` },
              { "@type": "ListItem", "position": 2, "name": "ShiftBridge", "item": `${SITE}/shiftbridge` },
              { "@type": "ListItem", "position": 3, "name": "PRN Shifts",  "item": url },
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": "PRN Shifts — All Specialties × Cities",
            "url": url,
            "description": "Directory of every PRN healthcare-staffing landing page on TRITAL ShiftBridge™.",
            "isPartOf": { "@type": "WebSite", "@id": `${SITE}/#website` },
            "hasPart": [
              ...SEO_SPECIALTIES.map((s) => ({ "@type": "WebPage", "name": `${s.full} PRN Shifts`, "url": `${url}/${s.slug}` })),
              ...TOP_CITIES.map((c) => ({ "@type": "WebPage", "name": `PRN Shifts in ${c.city}, ${c.state}`, "url": `${url}/${c.slug}` })),
            ],
          },
        ]}
      />

      {/* HERO */}
      <section className="relative overflow-hidden bg-trital-mesh border-b border-slate-200" data-testid="hub-hero">
        <div className="absolute inset-0 dot-grid opacity-50 pointer-events-none" />
        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-8 pt-14 pb-14 lg:pt-20 lg:pb-20">
          <nav className="flex items-center gap-1.5 text-[12.5px] text-slate-500 mb-5" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-trital-navy">Home</Link>
            <ChevronRight className="w-3 h-3" strokeWidth={1.8} />
            <Link to="/shiftbridge" className="hover:text-trital-navy">ShiftBridge</Link>
            <ChevronRight className="w-3 h-3" strokeWidth={1.8} />
            <span className="text-trital-navy font-semibold">PRN Shifts</span>
          </nav>

          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 shadow-card">
            <Sparkles className="w-3.5 h-3.5 text-trital-red" strokeWidth={1.8} />
            <span className="overline text-trital-navy">{totalUrls.toLocaleString()} PRN search categories · {TOP_CITIES.length} cities · {SEO_SPECIALTIES.length} specialties</span>
          </div>

          <h1
            className="mt-6 font-display font-black text-5xl lg:text-[64px] leading-[0.98] tracking-tight text-slate-900 text-balance max-w-5xl"
            data-testid="hub-h1"
          >
            PRN Shifts — every specialty, every top US healthcare market.
          </h1>
          <p className="mt-6 text-slate-700 max-w-3xl text-lg leading-relaxed">
            Browse open PRN shifts by specialty, by city, or by both. Every TRITAL ShiftBridge™ shift is W-2 with weekly pay, transparent posted rates, and zero clinician fees.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/shiftbridge/browse"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-trital-red text-white text-[13.5px] font-semibold btn-lift hover:bg-trital-red-deep"
              data-testid="hub-cta-browse"
            >
              Browse all open shifts <ArrowRight className="w-4 h-4" strokeWidth={2} />
            </Link>
            <Link
              to="/shiftbridge/signup/clinician"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white border border-slate-300 text-trital-navy text-[13.5px] font-semibold btn-lift hover:border-trital-navy"
              data-testid="hub-cta-signup"
            >
              Create free clinician account
            </Link>
          </div>
        </div>
      </section>

      {/* SPECIALTIES GRID */}
      <section className="bg-white border-b border-slate-200" data-testid="hub-specialties">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-16 lg:py-20">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <div className="overline text-trital-red">Browse by specialty</div>
              <h2 className="mt-2 font-display font-black text-3xl lg:text-4xl text-slate-900">
                {SEO_SPECIALTIES.length} PRN specialties · Nationwide
              </h2>
              <p className="mt-2 text-slate-600 text-sm max-w-2xl">
                See live PRN shifts for any specialty across all 50 states.
              </p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {SEO_SPECIALTIES.map((s, i) => (
              <motion.div
                key={s.slug}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: i * 0.02 }}
              >
                <Link
                  to={`/shiftbridge/prn-shifts/${s.slug}`}
                  className="group flex items-start gap-3 p-5 rounded-2xl bg-slate-50 border border-slate-200 hover:bg-white hover:border-trital-navy hover:shadow-card-hover transition-all"
                  data-testid={`hub-specialty-${s.slug}`}
                >
                  <div className="w-10 h-10 rounded-lg bg-trital-medical-blue-soft text-trital-navy flex items-center justify-center shrink-0 group-hover:bg-trital-red group-hover:text-white transition-colors">
                    <HeartPulse className="w-5 h-5" strokeWidth={1.8} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-display font-bold text-[14.5px] text-slate-900 group-hover:text-trital-navy">{s.name} PRN Shifts</div>
                    <div className="text-[12px] text-slate-500 mt-0.5 truncate">{s.full}</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-trital-red mt-1 shrink-0 group-hover:translate-x-0.5 transition-transform" strokeWidth={1.8} />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CITIES — grouped by region */}
      <section className="bg-slate-50 border-b border-slate-200" data-testid="hub-cities">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-16 lg:py-20">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <div className="overline text-trital-red">Browse by city</div>
              <h2 className="mt-2 font-display font-black text-3xl lg:text-4xl text-slate-900">
                {TOP_CITIES.length} top US healthcare markets
              </h2>
              <p className="mt-2 text-slate-600 text-sm max-w-2xl">
                Every metro has its own page with live PRN shift counts and city-specific FAQs.
              </p>
            </div>
          </div>

          <div className="space-y-10">
            {Object.entries(regions).map(([region, cities]) => (
              <div key={region}>
                <h3 className="font-display font-black text-xl text-trital-navy mb-4">{region}</h3>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {cities.map((c) => (
                    <Link
                      key={c.slug}
                      to={`/shiftbridge/prn-shifts/${c.slug}`}
                      className="group flex items-start gap-3 p-4 rounded-2xl bg-white border border-slate-200 hover:border-trital-navy hover:shadow-card-hover transition-all"
                      data-testid={`hub-city-${c.slug}`}
                    >
                      <MapPin className="w-4 h-4 text-trital-red mt-1 shrink-0" strokeWidth={1.8} />
                      <div className="min-w-0 flex-1">
                        <div className="font-display font-bold text-[14px] text-slate-900 group-hover:text-trital-navy">{c.city}, {c.state}</div>
                        <div className="text-[12px] text-slate-500 mt-0.5">{c.population} metro</div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMBO — Specialty × City matrix (the SEO heavyweight) */}
      <section className="bg-white border-b border-slate-200" data-testid="hub-combo">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-16 lg:py-20">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <div className="overline text-trital-red">Specialty × City — high-intent searches</div>
              <h2 className="mt-2 font-display font-black text-3xl lg:text-4xl text-slate-900">
                {(SEO_SPECIALTIES.length * TOP_CITIES.length).toLocaleString()} combo pages
              </h2>
              <p className="mt-2 text-slate-600 text-sm max-w-2xl">
                Every combination of specialty and city has its own dedicated SEO landing page.
              </p>
            </div>
          </div>

          <div className="space-y-12">
            {SEO_SPECIALTIES.map((s) => (
              <div key={s.slug} data-testid={`hub-combo-row-${s.slug}`}>
                <div className="flex items-baseline justify-between mb-4">
                  <h3 className="font-display font-black text-lg text-trital-navy">
                    <Link
                      to={`/shiftbridge/prn-shifts/${s.slug}`}
                      className="hover:text-trital-red transition-colors"
                    >
                      {s.full} PRN Shifts <span className="text-slate-400 font-normal">·</span>
                    </Link>
                  </h3>
                  <Link to={`/shiftbridge/prn-shifts/${s.slug}`} className="text-[12px] text-trital-navy hover:text-trital-red font-semibold inline-flex items-center gap-1">
                    See nationwide <ArrowRight className="w-3 h-3" strokeWidth={2} />
                  </Link>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-2">
                  {TOP_CITIES.map((c) => (
                    <Link
                      key={`${s.slug}-${c.slug}`}
                      to={`/shiftbridge/prn-shifts/${s.slug}-${c.slug}`}
                      className="text-[12.5px] text-slate-700 hover:text-trital-navy hover:bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-200 hover:border-trital-navy transition-colors"
                      data-testid={`hub-combo-${s.slug}-${c.slug}`}
                    >
                      <span className="font-semibold">{s.name}</span> · {c.city}, {c.state}
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-trital-navy-ink text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-trital-navy-mesh opacity-90 pointer-events-none" />
        <div className="absolute inset-0 dot-grid opacity-10 pointer-events-none" />
        <div className="relative max-w-[1100px] mx-auto px-5 lg:px-8 py-16 lg:py-20 text-center">
          <h2 className="font-display font-black text-3xl lg:text-5xl leading-[1.05] text-white text-balance">
            Find your PRN shift in <span className="text-trital-red">one tap.</span>
          </h2>
          <p className="mt-4 text-white/80 max-w-2xl mx-auto">
            Create a free TRITAL ShiftBridge™ clinician account. Browse, filter, and claim W-2 PRN shifts anywhere in the US — with transparent rates and weekly direct deposit.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/shiftbridge/signup/clinician"
              className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-trital-red text-white font-semibold btn-lift hover:bg-trital-red-deep"
              data-testid="hub-final-cta-signup"
            >
              Create free account <ArrowRight className="w-4 h-4" strokeWidth={2} />
            </Link>
            <Link
              to="/shiftbridge/browse"
              className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-white/10 border border-white/20 text-white font-semibold backdrop-blur hover:bg-white/15"
              data-testid="hub-final-cta-browse"
            >
              Browse open shifts
            </Link>
          </div>
        </div>
      </section>
    </ShiftsLayout>
  );
}
